import { computed, getCurrentInstance, onBeforeUnmount, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import {
  getCreatorAddressFromCreatorId,
  getCreatorCounts,
  getCreatorDetails,
  getMarketById,
  getMarketsByCreatorAddress,
  getMarketsByIds,
  getMarketRules,
  batchFetchMarketRules,
  subscribeToMarketUpdates,
  getContractAddress,
  clearMarketCache,
} from '@/common/predictionMarkets/predictionMarkets';
import type { CreatorDetails, Market, Outcome } from '@/common/predictionMarkets/types';
import {
  CREATOR_MARKETS_PAGE_SIZE,
  CREATOR_MARKETS_POLL_INTERVAL_MS,
  EVENT_DEDUPLICATION_WINDOW_MS,
  MARKET_CREATION_NOTIFICATION_TTL_MS,
} from '@/common/config';
import localDb from '@/common/localDb';

import { EVENTS, useEvents, type EventMap } from '@/composables/useEvents';
import { useClock } from '@/composables/useClock';
import { useBlockchainEvents } from '@/composables/useBlockchainEvents';
import { logDEBUG } from '@/common/debug';
import { isValidMarket, logError, logWarn } from '@/common/helpers';

export type CreatorMarketRecord = {
  id: string; // format: `${contractAddress}-${marketId}`
  contractAddress: string;
  creatorId: string;
  market: Market;
  isComplete: number;
  lastUpdatedAtMs: number;
};

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => Promise<T>): Promise<T> {
  const db = await localDb.openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(localDb.STORE_CREATOR_MARKETS, mode);
    const store = tx.objectStore(localDb.STORE_CREATOR_MARKETS);
    let result: T | null = null;
    let hasResult = false;

    run(store)
      .then((r) => {
        result = r;
        hasResult = true;
      })
      .catch(reject);

    tx.oncomplete = () => {
      db.close();
      if (!hasResult) reject(new Error('No result'));
      else resolve(result as T);
    };

    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

function cursorAll<T>(req: IDBRequest<IDBCursorWithValue | null>, limit?: number): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const out: T[] = [];
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return resolve(out);
      out.push(cursor.value as T);
      if (limit && out.length >= limit) return resolve(out);
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
  });
}

async function upsert(records: CreatorMarketRecord[]) {
  await withStore<void>('readwrite', async (store) => {
    for (const r of records) store.put(r);
    return;
  });
}

async function getByCreator(creatorId: string): Promise<CreatorMarketRecord[]> {
  const contractAddress = await getContractAddress();
  return withStore('readonly', async (store) => {
    const req = store.index(localDb.CREATOR_IDX_CREATOR).openCursor(IDBKeyRange.only(creatorId));
    const all = await cursorAll<CreatorMarketRecord>(req);
    // Filter by current contract address
    return all.filter((record) => record.contractAddress === contractAddress);
  });
}

async function getMarketRecord(marketId: string): Promise<CreatorMarketRecord | null> {
  const contractAddress = await getContractAddress();
  const recordId = `${contractAddress}-${marketId}`;
  return withStore('readonly', async (store) => {
    return new Promise<CreatorMarketRecord | null>((resolve, reject) => {
      const req = store.get(recordId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  });
}

async function getIncompleteOldestFirst(creatorId: string, max: number): Promise<CreatorMarketRecord[]> {
  const contractAddress = await getContractAddress();
  return withStore('readonly', async (store) => {
    const req = store.index(localDb.CREATOR_IDX_CREATOR_COMPLETE).openCursor(IDBKeyRange.only([creatorId, 0]));
    const all = await cursorAll<CreatorMarketRecord>(req);
    // Filter by current contract address
    return all
      .filter((record) => record.contractAddress === contractAddress)
      .sort((a, b) => a.lastUpdatedAtMs - b.lastUpdatedAtMs)
      .slice(0, max);
  });
}

// -------------------------------------------------
// Polling Queue
// -------------------------------------------------

type QueueItem = {
  id: string;
  priority: number;
  poll: () => Promise<void>;
  getOldestTs: () => Promise<number>;
};

const queue = ref<QueueItem[]>([]);
let interval: ReturnType<typeof setInterval> | null = null;
let inFlight = false;

// Lazy initialization to avoid circular dependency issues during module load
// Don't call useBlockchainEvents() at module level - it triggers circular deps
let blockchainEventsInstance: ReturnType<typeof useBlockchainEvents> | null = null;

function getBlockchainEvents() {
  if (!blockchainEventsInstance) {
    blockchainEventsInstance = useBlockchainEvents();
  }
  return blockchainEventsInstance;
}

function startQueue(intervalMs: number) {
  if (interval) return;

  interval = setInterval(async () => {
    // Skip polling if WebSocket is active (real-time updates available)
    if (getBlockchainEvents().isSubscribed.value) return;

    if (inFlight || queue.value.length === 0) return;
    inFlight = true;

    try {
      const enriched = await Promise.all(
        queue.value.map(async (q) => ({
          ...q,
          oldest: await q.getOldestTs(),
        })),
      );

      enriched.sort((a, b) => {
        if (b.priority !== a.priority) return b.priority - a.priority;
        return a.oldest - b.oldest;
      });

      const next = enriched[0];
      if (next) await next.poll();
    } finally {
      inFlight = false;
    }
  }, intervalMs);
}

function stopQueueIfEmpty() {
  if (queue.value.length === 0 && interval) {
    clearInterval(interval);
    interval = null;
  }
}

// -------------------------------------------------
// Helpers
// -------------------------------------------------

function isCompleteMarket(m: Market) {
  return m.isSettled || m.isVoid;
}

// ------------------------------------------------
// Global clock (singleton - shared across all instances)
// ------------------------------------------------

const { now } = useClock();

// ------------------------------------------------
// On-chain subscriber
// ------------------------------------------------

let isSubscribed = false;
// Track number of registered listeners to detect duplicate subscriptions
let subscriberCount = 0;

const creatorAddressToDetails = new Map<string, CreatorDetails>();

// Cache contract address for sync access in event handlers
let cachedContractAddress = '';

// ------------------------------------------------
// Global event listener registry (singleton pattern to avoid duplicate listeners)
// ------------------------------------------------

type EventCallbacks = {
  creatorId: string | null;
  onMarketCreated: (market: Market) => void | Promise<void>;
  onMarketSettled: (marketId: string, prefetchedMarket?: Market) => void;
  onMarketCancelled: (marketId: string, prefetchedMarket?: Market) => void;
  onMarketDataStale: (marketId: string) => void;
  onMarketDataUpdated: (marketId: string, prefetchedMarket?: Market) => void;
};

// Track all active instances and their event callbacks
const eventCallbackRegistry = new Map<symbol, EventCallbacks>();

// Track if we've registered global event listeners
let hasRegisteredGlobalEventListeners = false;

function registerGlobalEventListeners() {
  if (hasRegisteredGlobalEventListeners) return;
  hasRegisteredGlobalEventListeners = true;

  const events = useEvents();

  // Register singleton listeners that dispatch to all interested instances
  events.on(EVENTS.MARKET_CREATED, (payload) => {
    for (const callbacks of eventCallbackRegistry.values()) {
      // Only trigger for instances watching this creator
      if (callbacks.creatorId === payload.creatorId) {
        callbacks.onMarketCreated(payload.market);
      }
    }
  });

  events.on(EVENTS.MARKET_SETTLED, (payload) => {
    // Don't filter by creatorId - all instances should receive settlement events
    // This ensures participants get notifications even if they're not viewing the creator page
    // Each instance will check if the market belongs to them in the callback
    for (const callbacks of eventCallbackRegistry.values()) {
      callbacks.onMarketSettled(payload.market.id, payload.market);
    }
  });

  events.on(EVENTS.MARKET_CANCELLED, (payload) => {
    // Don't filter by creatorId - all instances should receive cancellation events
    // This ensures participants get notifications even if they're not viewing the creator page
    // Each instance will check if the market belongs to them in the callback
    for (const callbacks of eventCallbackRegistry.values()) {
      callbacks.onMarketCancelled(payload.market.id, payload.market);
    }
  });

  events.on(EVENTS.MARKET_DATA_STALE, (payload) => {
    for (const callbacks of eventCallbackRegistry.values()) {
      // Only trigger for instances watching this creator (now with creatorId filtering)
      if (callbacks.creatorId === payload.creatorId) {
        callbacks.onMarketDataStale(payload.marketId);
      }
    }
  });

  events.on(EVENTS.MARKET_DATA_UPDATED, (payload) => {
    for (const callbacks of eventCallbackRegistry.values()) {
      // Only trigger for instances watching this creator
      if (callbacks.creatorId === payload.creatorId) {
        callbacks.onMarketDataUpdated(payload.marketId, payload.market);
      }
    }
  });
}

/**
 * Clear the creator address-to-details cache (used for event processing)
 * Should be called on account switch or creator profile updates
 */
export function clearCreatorAddressToDetailsCache() {
  logDEBUG('[CACHE CLEAR]', 'useCreatorMarkets address-to-details cache');
  creatorAddressToDetails.clear();
}

async function getCDetails(creatorAddress: string): Promise<CreatorDetails> {
  // Events are validated at source (EvmPredictionMarketProvider filters zero addresses)
  // This function should never receive invalid addresses in normal operation
  if (creatorAddressToDetails.has(creatorAddress)) {
    return creatorAddressToDetails.get(creatorAddress)!;
  }

  const details = await getCreatorDetails(creatorAddress);
  creatorAddressToDetails.set(creatorAddress, details);
  return details;
}

async function subscribeToOnChainUpdates() {
  if (isSubscribed) {
    subscriberCount++;
    logDEBUG(`[SUBSCRIPTION] Already subscribed, skipping (${subscriberCount} total composable instances)`);
    return;
  }

  // Set synchronously before any await to prevent duplicate calls during async setup
  isSubscribed = true;
  subscriberCount = 1;

  logDEBUG('[SUBSCRIPTION] Registering blockchain event listeners');
  const events = useEvents();

  // Cache contract address for fast sync access
  if (!cachedContractAddress) {
    cachedContractAddress = await getContractAddress();
  }

  await subscribeToMarketUpdates(async (market) => {
    try {
      // Skip invalid markets (defensive check - should already be filtered by event listeners)
      if (!isValidMarket(market)) {
        logWarn('useCreatorMarkets', `skipping invalid market from event: ${market.id}, creator: ${market.creatorId}`);
        return;
      }

      // Parallelize operations that don't depend on each other (faster!)
      const [creatorDetails, existingRecord] = await Promise.all([getCDetails(market.creatorId), getMarketRecord(market.id)]);

      const creatorId = creatorDetails.id;
      const isNewMarket = !existingRecord;

      const record: CreatorMarketRecord = {
        id: `${cachedContractAddress}-${market.id}`,
        contractAddress: cachedContractAddress,
        creatorId,
        market,
        // Compute completion status from actual market state (settled or voided)
        // This ensures cancelled markets immediately show as complete
        isComplete: isCompleteMarket(market) ? 1 : 0,
        lastUpdatedAtMs: Date.now(),
      };

      // Emit appropriate event based on market state
      if (isNewMarket) {
        // Market not in local IndexedDB yet — it may have been created before the user started using the app.
        // Check actual market state so we emit the correct event:
        // - Already settled → MARKET_SETTLED (needed for prediction history updates for non-creator users)
        // - Already cancelled → MARKET_CANCELLED
        // - Truly new/active → MARKET_CREATED
        if (market.isSettled && !market.isVoid && market.winningOutcomeId) {
          const creatorFeesEarned = market.creatorFeesTotal;
          const winningOutcome = market.outcomes.find((o) => o.id === market.winningOutcomeId);
          events.emit(EVENTS.MARKET_SETTLED, {
            creatorId,
            creatorAddress: market.creatorId,
            market,
            winningOutcomeId: market.winningOutcomeId,
            winningOutcomeName: winningOutcome?.name || '',
            creatorFeesEarned,
          });
        } else if (market.isVoid) {
          events.emit(EVENTS.MARKET_CANCELLED, {
            creatorId,
            creatorAddress: market.creatorId,
            market,
          });
        }

        // Emit MARKET_CREATED for new active markets so the reactive UI updates immediately.
        // This is essential on mainnet where the polling queue is skipped when WebSocket is
        // subscribed — without this emit, markets.value is never updated and the new market
        // stays invisible until the user manually refreshes.
        if (!market.isSettled && !market.isVoid) {
          const now = Date.now();
          const lastEmitted = notifiedMarketCreations.get(market.id);
          if (!lastEmitted || now - lastEmitted > MARKET_CREATION_NOTIFICATION_TTL_MS) {
            notifiedMarketCreations.set(market.id, now);
            events.emit(EVENTS.MARKET_CREATED, {
              creatorId,
              creatorAddress: market.creatorId,
              market,
            });
          }
        }

        // Persist to IndexedDB in background (non-blocking for UI)
        upsert([record]).catch((err) => logError('useCreatorMarkets', 'persisting new market to IndexedDB', err));
      } else {
        // Existing market - persist first to ensure consistent state, then emit event
        await upsert([record]);

        // Check for state transitions
        const wasIncomplete = existingRecord.isComplete === 0;
        const isNowComplete = isCompleteMarket(market);

        if (wasIncomplete && isNowComplete && market.isSettled && !market.isVoid && market.winningOutcomeId) {
          // Market just settled - use pre-calculated fees from contract (much faster than looping)
          const creatorFeesEarned = market.creatorFeesTotal;
          const winningOutcome = market.outcomes.find((o) => o.id === market.winningOutcomeId);
          events.emit(EVENTS.MARKET_SETTLED, {
            creatorId,
            creatorAddress: market.creatorId,
            market,
            winningOutcomeId: market.winningOutcomeId,
            winningOutcomeName: winningOutcome?.name || '',
            creatorFeesEarned,
          });
        } else if (wasIncomplete && isNowComplete && market.isVoid) {
          // Market just cancelled
          events.emit(EVENTS.MARKET_CANCELLED, {
            creatorId,
            creatorAddress: market.creatorId,
            market,
          });
        } else {
          // Regular update (e.g., PlacedPrediction) - emit for reactive state updates
          // Pass the already-fetched market to skip redundant RPC calls
          events.emit(EVENTS.MARKET_DATA_UPDATED, {
            marketId: market.id,
            creatorId,
            market, // Pass pre-fetched market data
          });
        }
      }
    } catch (error) {
      // Skip markets with invalid creator addresses (e.g., zero address)
      logWarn('useCreatorMarkets', `skipping market ${market.id} due to invalid creator`, error);
      return;
    }
  });

  logDEBUG('[SUBSCRIPTION] Blockchain event listeners registered successfully');
}

// -------------------------------------------------
// Composable
// -------------------------------------------------

type Options = {
  pollIntervalMs?: number;
  autoStart?: boolean;
  maxPagesPerPass?: number;
  maxMarketPollsPerPass?: number;
  priority?: number;
};

// Global singleton: Track markets that have been notified about creation (never emit MARKET_CREATED twice for same market)
// This must be global to prevent re-emitting notifications when switching accounts
// Map stores marketId -> timestamp (cleanup after 24h to prevent unbounded growth)
const notifiedMarketCreations = new Map<string, number>();

// Global singleton: Track markets whose rules have been fetched (prevent re-fetching on every poll)
// Key: marketId, Value: true if rules have been fetched
// This prevents the expensive batch rule fetching from running repeatedly
const rulesFetchedForMarkets = new Map<string, boolean>();

export function useCreatorMarkets(creatorIdInput: MaybeRefOrGetter<string | null>, options: Options = {}) {
  const pollIntervalMs = options.pollIntervalMs ?? CREATOR_MARKETS_POLL_INTERVAL_MS;
  const autoStart = options.autoStart ?? true;
  const maxPagesPerPass = options.maxPagesPerPass ?? 5;
  const maxMarketPollsPerPass = options.maxMarketPollsPerPass ?? 20;
  const priority = options.priority ?? 0;

  const creatorId = computed(() => toValue(creatorIdInput));
  const creatorAddress = ref<string | null>(null);
  const markets = ref<CreatorMarketRecord[]>([]);
  const isLoading = ref(false);
  const isPolling = ref(false);
  const error = ref<string | null>(null);
  const lastKnownChainCount = ref(0);
  const hasLoadedAll = ref(false);
  const counts = ref<{ total: number; live: number; pending: number; completed: number } | null>(null);

  let localInFlight = false;
  let isInitialLoad = true;
  let contractAddressSync = ''; // Cached contract address for sync access

  // Event deduplication: Track emitted events with timestamps to prevent duplicates
  // Key format: "EVENT_TYPE:marketId" -> timestamp
  // Events are deduplicated to prevent multiple rapid notifications
  const emittedEvents = new Map<string, number>();
  const DEDUPLICATION_WINDOW_MS = EVENT_DEDUPLICATION_WINDOW_MS;

  const liveMarkets = computed(() =>
    markets.value.filter((marketRecord) => {
      const isLive = !marketRecord.market.isSettled && !marketRecord.market.isVoid;
      const isPending = now.value > marketRecord.market.deadline.getTime();
      return isLive && !isPending;
    }),
  );

  const pendingMarkets = computed(() =>
    markets.value.filter((marketRecord) => {
      const isPending = now.value > marketRecord.market.deadline.getTime();
      return !marketRecord.market.isSettled && !marketRecord.market.isVoid && isPending;
    }),
  );

  const completedMarkets = computed(() =>
    markets.value.filter((marketRecord) => {
      return marketRecord.market.isSettled || marketRecord.market.isVoid;
    }),
  );

  // Compute counts from loaded markets for instant display
  // These update reactively as markets load, no RPC needed
  const computedCounts = computed(() => ({
    total: markets.value.length,
    live: liveMarkets.value.length,
    pending: pendingMarkets.value.length,
    completed: completedMarkets.value.length,
  }));

  // Merge RPC counts with computed counts (hybrid strategy)
  // - live/pending: Use computedCounts (reactive to clock for deadline transitions)
  // - completed: Use RPC counts (shows total from blockchain, not just loaded markets)
  // - total: Use RPC when available (accurate total), fallback to computed
  const mergedCounts = computed(() => {
    // Hybrid approach when we have markets in memory
    if (markets.value.length > 0 && counts.value) {
      return {
        total: counts.value.total, // RPC total (accurate count from blockchain)
        live: computedCounts.value.live, // Computed (reactive to clock for deadline transitions)
        pending: computedCounts.value.pending, // Computed (reactive to clock for deadline transitions)
        completed: counts.value.completed, // RPC completed (shows total, not just loaded markets)
      };
    }

    // If we have markets but no RPC counts, use pure computed
    if (markets.value.length > 0) {
      return computedCounts.value;
    }

    // Only use pure RPC counts during initial load when we have zero markets
    // (Provides instant count display before markets are fetched)
    if (counts.value) {
      return counts.value;
    }

    // Final fallback to computed counts
    return computedCounts.value;
  });

  async function resolveAddress() {
    if (!creatorId.value) return;
    creatorAddress.value = await getCreatorAddressFromCreatorId(creatorId.value);
  }

  function computeMaxPage(total: number, size: number): number {
    if (total <= 0) return 0;
    return Math.max(0, Math.ceil(total / size) - 1);
  }

  // Helper to emit events only once per market+event-type combination within a time window
  function emitEventOnce<K extends keyof EventMap>(eventType: K, marketId: string, payload: EventMap[K]) {
    const key = `${eventType}:${marketId}`;
    const now = Date.now();
    const lastEmitted = emittedEvents.get(key);

    // Only emit if not emitted recently (within deduplication window)
    if (!lastEmitted || now - lastEmitted > DEDUPLICATION_WINDOW_MS) {
      emittedEvents.set(key, now);
      const events = useEvents();
      events.emit(eventType, payload);

      // Clean up old entries to prevent memory leak
      // Remove entries older than 2x the deduplication window
      const cutoff = now - DEDUPLICATION_WINDOW_MS * 2;
      for (const [k, timestamp] of emittedEvents.entries()) {
        if (timestamp < cutoff) {
          emittedEvents.delete(k);
        }
      }
    }
  }

  /**
   * Preload market rules in the background (non-blocking)
   * This ensures rules are ready when user clicks "View Rules"
   * Lazy load in UI components acts as fallback if user clicks before preload completes
   *
   * Note: Individual lazy loads have in-flight tracking to prevent duplicate requests
   */
  async function preloadMarketRules(market: Market): Promise<void> {
    if (!isValidMarket(market) || market.rules !== '') return; // Skip if invalid or already loaded

    try {
      // Uses in-flight tracking internally, so safe to call even if already loading
      const rules = await getMarketRules(market.id);
      // Update the market object reactively
      market.rules = rules;
      logDEBUG('preloadMarketRules', `Preloaded rules for market ${market.id}`);
    } catch (error) {
      // Silent failure - lazy load will handle it when user clicks
      logError('useCreatorMarkets', `preloading rules for market ${market.id}`, error);
    }
  }

  async function fetchPages(chainCount: number): Promise<boolean> {
    if (!creatorAddress.value) return false;

    lastKnownChainCount.value = chainCount;

    const maxPage = computeMaxPage(chainCount, CREATOR_MARKETS_PAGE_SIZE);

    const local = await getByCreator(creatorId.value || '');
    const existingIds = new Set(local.map((r) => r.id));
    const maxLocalPage = Math.max(0, Math.ceil(local.length / CREATOR_MARKETS_PAGE_SIZE));

    const pages: number[] = [];

    if (maxPage >= 0) {
      pages.push(1);
    }

    for (let p = maxLocalPage; p <= maxPage && pages.length <= maxPagesPerPass; p++) {
      pages.push(p + 1);
    }

    const now = Date.now();
    const fetched: Market[] = [];

    // Fetch all pages in parallel for better performance
    const address = creatorAddress.value!; // Already checked at function start
    const pagePromises = pages.map((page) => getMarketsByCreatorAddress(address, page));
    const results = await Promise.all(pagePromises);

    for (const result of results) {
      // Filter out invalid markets (market ID 0, zero address creators)
      const validMarkets = result.filter(isValidMarket);
      fetched.push(...validMarkets);
    }

    const contractAddress = await getContractAddress();

    // Collect new market events to emit after database update
    const newMarketEvents: Array<{
      creatorId: string;
      creatorAddress: string;
      market: Market;
    }> = [];

    // Collect settlement/cancellation events to emit after database update
    const settledEvents: Array<{
      creatorId: string;
      creatorAddress: string;
      market: Market;
      winningOutcomeId: string;
      winningOutcomeName: string;
      creatorFeesEarned: number;
    }> = [];

    const cancelledEvents: Array<{
      creatorId: string;
      creatorAddress: string;
      market: Market;
    }> = [];

    const records: CreatorMarketRecord[] = fetched.map((m) => {
      // Compare using just the market ID, not the composite ID
      const existingRecord = local.find((r) => {
        const recordMarketId = r.id.split('-')[1];
        return recordMarketId === m.id;
      });

      // Detect new markets - only emit for real-time updates, not initial load
      if (!isInitialLoad && !existingIds.has(`${contractAddress}-${m.id}`) && creatorAddress.value) {
        newMarketEvents.push({
          creatorId: creatorId.value || '',
          creatorAddress: creatorAddress.value,
          market: m,
        });
      }

      // Detect settlement/cancellation transitions for existing markets
      if (existingRecord && !isInitialLoad && creatorAddress.value) {
        const wasIncomplete = existingRecord.isComplete === 0;
        const isNowComplete = isCompleteMarket(m);

        // Market settlement
        if (wasIncomplete && isNowComplete && m.isSettled && !m.isVoid && m.winningOutcomeId) {
          // Use pre-calculated fees from contract (much faster than looping)
          const creatorFeesEarned = m.creatorFeesTotal;
          const winningOutcome = m.outcomes.find((o) => o.id === m.winningOutcomeId);

          settledEvents.push({
            creatorId: creatorId.value || '',
            creatorAddress: creatorAddress.value,
            market: m,
            winningOutcomeId: m.winningOutcomeId,
            winningOutcomeName: winningOutcome?.name || '',
            creatorFeesEarned,
          });
        }

        // Market cancellation
        if (wasIncomplete && isNowComplete && m.isVoid) {
          cancelledEvents.push({
            creatorId: creatorId.value || '',
            creatorAddress: creatorAddress.value,
            market: m,
          });
        }
      }

      return {
        id: `${contractAddress}-${m.id}`,
        contractAddress,
        creatorId: creatorId.value || '',
        market: m,
        isComplete: isCompleteMarket(m) ? 1 : 0,
        lastUpdatedAtMs: now,
      };
    });

    await upsert(records);

    // Only emit events AFTER database update completes
    for (const payload of newMarketEvents) {
      // Only emit MARKET_CREATED once per market (with 24h TTL to prevent unbounded growth)
      const now = Date.now();
      const lastEmitted = notifiedMarketCreations.get(payload.market.id);

      if (!lastEmitted || now - lastEmitted > MARKET_CREATION_NOTIFICATION_TTL_MS) {
        notifiedMarketCreations.set(payload.market.id, now);
        const events = useEvents();
        events.emit(EVENTS.MARKET_CREATED, payload);

        // Clean up old entries to prevent memory leak
        const cutoff = now - MARKET_CREATION_NOTIFICATION_TTL_MS * 2;
        for (const [marketId, timestamp] of notifiedMarketCreations.entries()) {
          if (timestamp < cutoff) {
            notifiedMarketCreations.delete(marketId);
          }
        }
      }
    }
    for (const payload of settledEvents) {
      emitEventOnce(EVENTS.MARKET_SETTLED, payload.market.id, payload);
    }
    for (const payload of cancelledEvents) {
      emitEventOnce(EVENTS.MARKET_CANCELLED, payload.market.id, payload);
    }

    return maxPage === maxLocalPage - 1;
  }

  async function refreshIncompleteMarkets() {
    const rows = await getIncompleteOldestFirst(creatorId.value || '', maxMarketPollsPerPass);

    if (rows.length === 0) return;

    // Extract just the market IDs from composite IDs (format: ${contractAddress}-${marketId})
    // The market ID is the last part after splitting by '-'
    const idsToFetch = rows
      .map((r) => {
        const parts = r.id.split('-');
        const marketId = parts[parts.length - 1];
        return marketId && marketId.length > 0 ? marketId : null;
      })
      .filter((id): id is string => id !== null);

    if (idsToFetch.length === 0) return;

    const updatedMarkets = await getMarketsByIds(idsToFetch);
    // Filter out invalid markets (market ID 0, zero address creators)
    const validUpdatedMarkets: Record<string, Market> = {};
    for (const [id, market] of Object.entries(updatedMarkets)) {
      if (isValidMarket(market)) {
        validUpdatedMarkets[id] = market;
      }
    }
    const now = Date.now();

    // Collect events to emit after database update
    const settledEvents: Array<{
      creatorId: string;
      creatorAddress: string;
      market: Market;
      winningOutcomeId: string;
      winningOutcomeName: string;
      creatorFeesEarned: number;
    }> = [];

    const cancelledEvents: Array<{
      creatorId: string;
      creatorAddress: string;
      market: Market;
    }> = [];

    const updated: CreatorMarketRecord[] = rows
      .map((r) => {
        // Extract market ID from composite ID (format: ${contractAddress}-${marketId})
        const parts = r.id.split('-');
        const marketId = parts[parts.length - 1] || '';
        if (!marketId) return null;

        const m = validUpdatedMarkets[marketId];
        if (!m) return null;

        // Detect market settlement transition
        const wasIncomplete = r.isComplete === 0;
        const isNowComplete = isCompleteMarket(m);

        // Only collect events for real-time updates, not during initial load
        if (!isInitialLoad && wasIncomplete && isNowComplete && m.isSettled && !m.isVoid && creatorAddress.value && m.winningOutcomeId) {
          // Use pre-calculated fees from contract (much faster than looping)
          const creatorFeesEarned = m.creatorFeesTotal;

          const winningOutcome = m.outcomes.find((o: Outcome) => o.id === m.winningOutcomeId);

          // Collect event to emit later
          settledEvents.push({
            creatorId: creatorId.value || '',
            creatorAddress: creatorAddress.value,
            market: m,
            winningOutcomeId: m.winningOutcomeId,
            winningOutcomeName: winningOutcome?.name || '',
            creatorFeesEarned,
          });
        }

        // Detect market cancellation transition
        // Only collect events for real-time updates, not during initial load
        if (!isInitialLoad && wasIncomplete && isNowComplete && m.isVoid && creatorAddress.value) {
          // Collect event to emit later
          cancelledEvents.push({
            creatorId: creatorId.value || '',
            creatorAddress: creatorAddress.value,
            market: m,
          });
        }

        return {
          ...r,
          market: m,
          isComplete: isCompleteMarket(m) ? 1 : 0,
          lastUpdatedAtMs: now,
        };
      })
      .filter(Boolean) as CreatorMarketRecord[];

    if (updated.length) {
      await upsert(updated);
      // Only emit events AFTER database update completes
      for (const payload of settledEvents) {
        emitEventOnce(EVENTS.MARKET_SETTLED, payload.market.id, payload);
      }
      for (const payload of cancelledEvents) {
        emitEventOnce(EVENTS.MARKET_CANCELLED, payload.market.id, payload);
      }
    }
  }

  async function loadLocal() {
    const local = await getByCreator(creatorId.value || '');

    // Deduplicate by market ID - keep only the most recently updated record for each market
    const deduped = new Map<string, CreatorMarketRecord>();
    for (const record of local) {
      // Skip invalid markets (defensive check for corrupted IndexedDB data)
      if (!isValidMarket(record.market)) {
        logWarn('useCreatorMarkets', `skipping invalid market from IndexedDB: ${record.market.id}`);
        continue;
      }
      const key = record.market.id;
      const existing = deduped.get(key);
      // Keep the record with the latest update timestamp
      if (!existing || record.lastUpdatedAtMs > existing.lastUpdatedAtMs) {
        deduped.set(key, record);
      }
    }

    // Sort by market ID in descending order
    markets.value = Array.from(deduped.values()).sort((a, b) => Number(b.market.id) - Number(a.market.id));

    // Trigger immediate refetch for markets with failed blob loading
    // This ensures they get refreshed with the new malformed blob detection
    const marketsNeedingRefresh = markets.value.filter((record) => record.market.rules.includes('Important: Failed to load rules!'));

    if (marketsNeedingRefresh.length > 0) {
      // Fire and forget - don't await to avoid blocking
      Promise.all(marketsNeedingRefresh.map((record) => pollOneMarket(record.market.id))).catch((err) =>
        logError('useCreatorMarkets', 'refreshing stale markets', err),
      );
    }

    // Preload rules for markets with empty rules (background fetch, non-blocking)
    // Only fetch rules that haven't been fetched before
    const marketsNeedingRules = markets.value.filter((record) => {
      // Skip if rules already loaded in memory
      if (record.market.rules !== '') return false;
      // Skip if we've already fetched (prevents re-fetching on every poll)
      if (rulesFetchedForMarkets.has(record.market.id)) return false;
      return true;
    });

    if (marketsNeedingRules.length > 0) {
      // Mark as fetched BEFORE making the request to prevent duplicate requests
      marketsNeedingRules.forEach((record) => rulesFetchedForMarkets.set(record.market.id, true));

      // Use batch fetching for efficiency
      const marketsList = marketsNeedingRules.map((record) => record.market);
      batchFetchMarketRules(marketsList).catch((err) => {
        // On failure, remove from fetched set so we retry later
        marketsNeedingRules.forEach((record) => rulesFetchedForMarkets.delete(record.market.id));
        logError('useCreatorMarkets', 'batch preloading rules', err);
      });
    }
  }

  async function updateCounts() {
    if (!creatorAddress.value) return;

    const newCounts = await getCreatorCounts(creatorAddress.value);

    counts.value = {
      total: newCounts.all,
      live: newCounts.live,
      pending: newCounts.pending,
      // Completed = all - live - pending (includes both settled AND cancelled/void markets)
      completed: Math.max(0, newCounts.all - newCounts.live - newCounts.pending),
    };
  }

  async function pollOnce() {
    if (!creatorAddress.value || localInFlight) return;

    // Always refresh counts first so the freshness check below uses up-to-date RPC totals.
    // Without this, a newly-created market can stay invisible indefinitely: the WS fires
    // (setting isSubscribed=true and skipping the polling queue), but if the WS event is
    // lost/late the only recovery is pollOnce — which would incorrectly skip fetchPages
    // because stale counts make localData.length >= rpcTotal appear true.
    await updateCounts().catch((err) => logError('useCreatorMarkets', 'updating counts in pollOnce', err));

    // Get local data to check freshness
    const localData = await getByCreator(creatorId.value || '');

    // Check IndexedDB freshness before hitting RPC for full market data
    // Only fetch from chain if local data is stale (>30s old)
    if (localData.length > 0) {
      const mostRecentUpdate = Math.max(...localData.map((r) => r.lastUpdatedAtMs));
      const ageMs = Date.now() - mostRecentUpdate;

      // If data is fresh (<30s old), skip expensive fetchPages and just load from IndexedDB
      // BUT only if IndexedDB has all markets (compare with RPC count to detect new markets)
      const rpcTotal = counts.value?.total ?? 0;
      if (ageMs < pollIntervalMs && localData.length >= rpcTotal) {
        await loadLocal();
        // Mark as loaded since IndexedDB has complete data (verified by count match)
        hasLoadedAll.value = true;
        return;
      }
    }

    localInFlight = true;

    try {
      // Use freshly-fetched counts
      const chainCount = counts.value?.total ?? 0;

      // Parallelize fetchPages and refreshIncompleteMarkets
      const [hasLoaded] = await Promise.all([fetchPages(chainCount), refreshIncompleteMarkets()]);

      hasLoadedAll.value = hasLoaded;
      // loadLocal must run after DB writes from fetchPages/refreshIncompleteMarkets
      await loadLocal();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
    } finally {
      localInFlight = false;
    }
  }

  async function refresh() {
    isLoading.value = true;
    try {
      // Initialize contract address for sync access
      if (!contractAddressSync) {
        contractAddressSync = await getContractAddress();
      }

      // IMPORTANT: Resolve address FIRST before updating counts
      await resolveAddress();

      // Now update counts and load local data in parallel
      await Promise.all([
        updateCounts().catch((err) => logError('useCreatorMarkets', 'updating counts', err)),
        loadLocal(), // Load from IndexedDB for instant display
      ]);

      // Now fetch fresh market data in background (don't await - this can take seconds)
      // Vue will batch any updates, but at least counts are already shown
      pollOnce().catch((err) => logError('useCreatorMarkets', 'polling for market updates', err));
    } finally {
      isLoading.value = false;
      // After first refresh completes, we're no longer in initial load
      isInitialLoad = false;
    }
  }

  const queueId = computed(() => `creator-${creatorId.value}`);

  function startPolling() {
    if (isPolling.value) return;
    isPolling.value = true;

    const existing = queue.value.find((q) => q.id === queueId.value);
    if (!existing) {
      queue.value.push({
        id: queueId.value,
        priority,
        poll: pollOnce,
        getOldestTs: async () => {
          const rows = await getIncompleteOldestFirst(creatorId.value || '', 1);
          return rows[0]?.lastUpdatedAtMs ?? 0;
        },
      });
    }

    startQueue(pollIntervalMs);
  }

  function stopPolling() {
    isPolling.value = false;
    queue.value = queue.value.filter((q) => q.id !== queueId.value);
    stopQueueIfEmpty();
  }

  async function pollOneMarket(marketId: string, prefetchedMarket?: Market, skipPersist = false) {
    // Skip invalid market IDs (market ID 0 is often a placeholder)
    if (!marketId || marketId === '0') {
      logWarn('useCreatorMarkets', `skipping invalid market ID: ${marketId}`);
      return;
    }

    // Use prefetched market if provided (from blockchain event) to skip redundant RPC call
    const updatedMarket = prefetchedMarket || (await getMarketById(marketId));

    if (!updatedMarket) return;

    // Validate the fetched market
    if (!isValidMarket(updatedMarket)) {
      logWarn('useCreatorMarkets', `skipping invalid market: ${updatedMarket.id}, creator: ${updatedMarket.creatorId}`);
      return;
    }

    const contractAddress = await getContractAddress();

    // Get existing record to check for state transitions
    const existingRecords = await getByCreator(creatorId.value || '');
    const existingRecord = existingRecords.find((r) => r.market.id === marketId);

    const record: CreatorMarketRecord = {
      id: `${contractAddress}-${updatedMarket.id}`,
      contractAddress,
      creatorId: creatorId.value || '',
      market: updatedMarket,
      isComplete: isCompleteMarket(updatedMarket) ? 1 : 0,
      lastUpdatedAtMs: Date.now(),
    };

    // Skip IndexedDB write if already done by global listener (skipPersist=true)
    // This eliminates redundant writes when called from event handlers
    if (!skipPersist) {
      await upsert([record]);
    }

    // Detect state transitions and emit events (settlement or cancellation)
    if (existingRecord && creatorAddress.value) {
      const wasIncomplete = existingRecord.isComplete === 0;
      const isNowComplete = isCompleteMarket(updatedMarket);

      // Detect market settlement transition
      if (wasIncomplete && isNowComplete && updatedMarket.isSettled && !updatedMarket.isVoid && updatedMarket.winningOutcomeId) {
        // Use pre-calculated fees from contract (much faster than looping)
        const creatorFeesEarned = updatedMarket.creatorFeesTotal;
        const winningOutcome = updatedMarket.outcomes.find((o) => o.id === updatedMarket.winningOutcomeId);

        emitEventOnce(EVENTS.MARKET_SETTLED, updatedMarket.id, {
          creatorId: creatorId.value || '',
          creatorAddress: creatorAddress.value,
          market: updatedMarket,
          winningOutcomeId: updatedMarket.winningOutcomeId,
          winningOutcomeName: winningOutcome?.name || '',
          creatorFeesEarned,
        });
      }

      // Detect market cancellation transition
      if (wasIncomplete && isNowComplete && updatedMarket.isVoid) {
        emitEventOnce(EVENTS.MARKET_CANCELLED, updatedMarket.id, {
          creatorId: creatorId.value || '',
          creatorAddress: creatorAddress.value,
          market: updatedMarket,
        });
      }
    }

    // Update the specific market in the reactive array instead of reloading all markets
    // This ensures Vue detects the change and triggers watchers in child components
    const index = markets.value.findIndex((m) => m.market.id === marketId);
    if (index !== -1) {
      // Use splice to ensure Vue detects the array mutation
      markets.value.splice(index, 1, record);
    } else {
      // Market not found in current list - reload all to handle new markets
      await loadLocal();
    }
  }

  // Register singleton global event listeners (done once at module level)
  registerGlobalEventListeners();

  // Create unique identifier for this instance
  const instanceId = Symbol('useCreatorMarkets-instance');

  // Register this instance's callbacks with the global event system
  // This allows one set of event listeners to dispatch to multiple instances
  eventCallbackRegistry.set(instanceId, {
    get creatorId() {
      return creatorId.value;
    },
    onMarketCreated: (market: Market) => {
      // Deduplicate - check if market already exists in array
      const existingIndex = markets.value.findIndex((m) => m.market.id === market.id);
      if (existingIndex !== -1) {
        logDEBUG(`[useCreatorMarkets] Skipping duplicate market: ${market.id}`);
        return;
      }

      // Market already persisted to IndexedDB by global listener, just add to reactive array
      const record: CreatorMarketRecord = {
        id: `${contractAddressSync}-${market.id}`,
        contractAddress: contractAddressSync,
        creatorId: creatorId.value || '',
        market,
        isComplete: isCompleteMarket(market) ? 1 : 0,
        lastUpdatedAtMs: Date.now(),
      };

      markets.value.unshift(record);

      // Preload rules in background if they're empty (non-blocking)
      if (market.rules === '') {
        preloadMarketRules(market).catch((err) => logError('useCreatorMarkets', 'preloading rules for new market', err));
      }

      // No need to manually update counts - computedCounts automatically reflects the new market
      // This prevents stale count issues when markets transition between live/pending over time
    },
    onMarketSettled: (marketId: string, prefetchedMarket?: Market) => {
      // Only process if this market belongs to the creator we're watching
      const hasMarket = markets.value.some((m) => m.market.id === marketId);
      if (!hasMarket) return;

      // Use prefetched market to skip redundant RPC call
      // Global listener already persisted to IndexedDB, so skip redundant write
      void pollOneMarket(marketId, prefetchedMarket, true);

      // Refresh counts to update pending/completed tabs
      void updateCounts();
    },
    onMarketCancelled: (marketId: string, prefetchedMarket?: Market) => {
      // Only process if this market belongs to the creator we're watching
      const hasMarket = markets.value.some((m) => m.market.id === marketId);
      if (!hasMarket) return;

      // Use prefetched market to skip redundant RPC call
      // Global listener already persisted to IndexedDB, so skip redundant write
      void pollOneMarket(marketId, prefetchedMarket, true);

      // Refresh counts to update live/pending/completed tabs
      void updateCounts();
    },
    onMarketDataStale: (marketId: string) => {
      // MARKET_DATA_STALE is emitted when user makes a prediction
      // Clear cache and fetch to show optimistic update
      clearMarketCache([marketId], 'useCreatorMarkets MARKET_DATA_STALE');
      void pollOneMarket(marketId);
    },
    onMarketDataUpdated: (marketId: string, prefetchedMarket?: Market) => {
      // MARKET_DATA_UPDATED is emitted when blockchain confirms the update (e.g., PlacedPrediction event)
      // Use prefetched market to skip redundant RPC call
      // Global listener already persisted to IndexedDB, so skip redundant write
      void pollOneMarket(marketId, prefetchedMarket, true);
    },
  });

  // Register lifecycle hooks BEFORE any async operations (like watch with immediate: true)
  // Only register if there's an active component instance (e.g., not called inside watch callbacks)
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      stopPolling();
      // Remove this instance from the global event callback registry
      eventCallbackRegistry.delete(instanceId);
    });
  }

  watch(
    creatorId,
    async (next) => {
      await subscribeToOnChainUpdates();

      stopPolling();
      markets.value = [];
      error.value = null;
      lastKnownChainCount.value = 0;
      counts.value = null; // Reset counts
      hasLoadedAll.value = false; // Reset to prevent stale state showing in tabs
      isInitialLoad = true; // Reset initial load flag when creator changes

      if (!next) return;

      await refresh();
      if (autoStart) startPolling();
    },
    { immediate: true },
  );

  return {
    creatorId: computed(() => creatorId),
    creatorAddress,
    markets,
    counts: mergedCounts, // Use merged counts (RPC when available, computed from markets otherwise)
    now,
    liveMarkets,
    pendingMarkets,
    completedMarkets,
    hasCachedData: computed(() => markets.value.length > 0),
    lastKnownChainCount,
    isLoading,
    isPolling,
    hasLoadedAll,
    error,
    refresh,
    pollOnce,
    startPolling,
    stopPolling,
    pollOneMarket,
  };
}
